import React, { useEffect } from 'react';

/**
 * SiteCursorStyle — swaps the actual browser pointer for the whole public
 * site by attaching a <style> tag with `cursor:` overrides. Kept as a
 * separate concern from the trail so admin can toggle them independently.
 *
 * Supported presets:
 *   default     – native browser arrow (no override)
 *   crosshair   – native crosshair (built-in, precise)
 *   sniper      – custom SVG reticle with red center dot
 *   bullet_dot  – small red dot (like a laser sight)
 *
 * All custom cursors ship with a solid fallback so any browser lacking
 * SVG cursor support falls back to a sensible native option.
 */

const svgToUrl = (svg) => {
  // Encode as base64 to avoid apostrophe/quote breakage inside the CSS url(...) declaration.
  // The SVG constants below use single-quoted attributes; wrapping in single-quoted url() and
  // relying on encodeURIComponent leaves those apostrophes intact, which prematurely terminates
  // the url() and causes browsers to drop the whole cursor rule (silent, unstyled fallback).
  const b64 = (typeof window !== 'undefined' && window.btoa)
    ? window.btoa(unescape(encodeURIComponent(svg)))
    : Buffer.from(svg, 'utf-8').toString('base64');
  return `url("data:image/svg+xml;base64,${b64}") 12 12`;
};

const SNIPER_SVG = `<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28'>
<circle cx='14' cy='14' r='11' fill='none' stroke='rgba(239,68,68,0.95)' stroke-width='1.4'/>
<circle cx='14' cy='14' r='6.5' fill='none' stroke='rgba(239,68,68,0.85)' stroke-width='1'/>
<line x1='14' y1='1' x2='14' y2='9' stroke='rgba(239,68,68,0.95)' stroke-width='1.3'/>
<line x1='14' y1='19' x2='14' y2='27' stroke='rgba(239,68,68,0.95)' stroke-width='1.3'/>
<line x1='1' y1='14' x2='9' y2='14' stroke='rgba(239,68,68,0.95)' stroke-width='1.3'/>
<line x1='19' y1='14' x2='27' y2='14' stroke='rgba(239,68,68,0.95)' stroke-width='1.3'/>
<circle cx='14' cy='14' r='1.6' fill='rgba(239,68,68,1)'/>
</svg>`;

const BULLET_DOT_SVG = `<svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20'>
<circle cx='10' cy='10' r='6' fill='rgba(239,68,68,0.85)' stroke='rgba(255,255,255,0.9)' stroke-width='1.2'/>
<circle cx='10' cy='10' r='2' fill='rgba(255,255,255,0.95)'/>
</svg>`;

const RULE_ID = 'site-cursor-style-rule';

const buildCss = (styleKey) => {
  switch (styleKey) {
    case 'crosshair':
      return `body, body * { cursor: crosshair !important; }
              body a, body button, body [role="button"], body input, body textarea, body select { cursor: pointer !important; }
              body input[type="text"], body input[type="email"], body input[type="number"], body input[type="tel"], body input[type="search"], body input[type="password"], body textarea { cursor: text !important; }`;
    case 'sniper':
      return `body, body * { cursor: ${svgToUrl(SNIPER_SVG)}, crosshair !important; }
              body a, body button, body [role="button"], body input, body textarea, body select { cursor: ${svgToUrl(SNIPER_SVG)}, pointer !important; }`;
    case 'bullet_dot':
      return `body, body * { cursor: ${svgToUrl(BULLET_DOT_SVG)}, crosshair !important; }
              body a, body button, body [role="button"], body input, body textarea, body select { cursor: ${svgToUrl(BULLET_DOT_SVG)}, pointer !important; }`;
    case 'default':
    default:
      return '';
  }
};

const SiteCursorStyle = ({ styleKey = 'default' }) => {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    let el = document.getElementById(RULE_ID);
    if (!el) {
      el = document.createElement('style');
      el.id = RULE_ID;
      document.head.appendChild(el);
    }
    el.textContent = buildCss(styleKey);
    return () => {
      // Clear on unmount so admin/estimate views regain the native cursor
      const stale = document.getElementById(RULE_ID);
      if (stale) stale.textContent = '';
    };
  }, [styleKey]);
  return null;
};

export default SiteCursorStyle;
